import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Plus, Trash2, ExternalLink, LogOut, Scale, ChevronDown, Inbox, FileText, Download } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { adminApi } from "../../lib/api";
import PageLinkSelect from "../../components/admin/PageLinkSelect";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../../components/ui/button";
import logoLight from "../../assets/cass-logo-light.png";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const Field = ({ label, value, onChange, area, placeholder }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-slate-500 mb-1">{label}</span>
    {area
      ? <textarea rows={3} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      : <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />}
  </label>
);

const Lines = ({ label, value, onChange }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-slate-500 mb-1">{label} <span className="text-slate-300">(one per line)</span></span>
    <textarea rows={4} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trimStart()).filter((s) => s !== ""))} className={inputCls} />
  </label>
);

const Card = ({ title, children, open: initOpen = false }) => {
  const [open, setOpen] = useState(initOpen);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`lca-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <h2 className="font-display font-bold text-navy">{title}</h2>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-6 pt-2 border-t border-slate-100 space-y-4">{children}</div>}
    </div>
  );
};

const Repeat = ({ items, onChange, template, render, addLabel }) => (
  <div className="space-y-3">
    {(items || []).map((it, i) => (
      <div key={i} className="relative bg-slate-50 rounded-xl p-4 border border-slate-100">
        <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
        <div className="space-y-3 pr-6">{render(it, (patch) => onChange(items.map((x, xi) => (xi === i ? { ...x, ...patch } : x))))}</div>
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => onChange([...(items || []), { ...template }])} className="rounded-full"><Plus size={14} className="mr-1" /> {addLabel}</Button>
  </div>
);

const setPath = (obj, key, val) => ({ ...obj, [key]: val });

const PdfUpload = ({ value, filename, onChange }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.uploadDocument(fd);
      onChange(res.url, res.filename);
      toast({ title: "PDF uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Only PDF/Word/Excel up to 25MB", variant: "destructive" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <span className="block text-xs font-semibold text-slate-500 mb-1">Upload PDF (takes priority over the guide slug below)</span>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-navy hover:bg-slate-50 transition-colors" data-testid="lca-lead-magnet-upload">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
          {value ? "Replace PDF" : "Upload PDF"}
          <input type="file" accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFile} className="hidden" disabled={uploading} />
        </label>
        {value && (
          <>
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-teal font-semibold inline-flex items-center gap-1 hover:underline"><ExternalLink size={13} /> {filename || "View file"}</a>
            <button type="button" onClick={() => onChange("", "")} className="text-xs text-red-500 hover:underline">Remove</button>
          </>
        )}
      </div>
    </div>
  );
};

function ContentEditor() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lmStats, setLmStats] = useState(null);

  useEffect(() => {
    adminApi.legalCashiering.get().then(setForm).catch(() => {});
    adminApi.legalCashiering.leadMagnetStats().then(setLmStats).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => setPath(f, k, v));
  const setObj = (k, sub, v) => setForm((f) => ({ ...f, [k]: setPath(f[k] || {}, sub, v) }));

  const save = async () => {
    setSaving(true);
    try { await adminApi.legalCashiering.save(form); toast({ title: "Saved", description: "Legal Cashiering content updated." }); }
    catch { toast({ title: "Save failed", description: "Please try again.", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const exportLeads = async () => {
    try {
      const leads = await adminApi.legalCashiering.leadMagnetLeads();
      if (!leads.length) { toast({ title: "No downloads to export yet" }); return; }
      const rows = [["Name", "Email", "Resource", "Date"], ...leads.map((l) => [l.name, l.email, l.guide_title, l.created_at])];
      const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = "lc-checklist-leads.csv";
      a.click();
    } catch { toast({ title: "Export failed", variant: "destructive" }); }
  };

  if (!form) return <div className="text-slate-400 py-10 text-center">Loading…</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={save} disabled={saving} data-testid="lca-save" className="bg-orange hover:bg-orange-dark text-white rounded-full">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save changes
        </Button>
      </div>

      <Card title="Hero" open>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.hero?.eyebrow} onChange={(v) => setObj("hero", "eyebrow", v)} />
          <Field label="Title" value={form.hero?.title} onChange={(v) => setObj("hero", "title", v)} />
        </div>
        <Field label="Subtitle" value={form.hero?.subtitle} onChange={(v) => setObj("hero", "subtitle", v)} area />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="CTA label" value={form.hero?.cta_label} onChange={(v) => setObj("hero", "cta_label", v)} />
          <Field label="CTA link" value={form.hero?.cta_link} onChange={(v) => setObj("hero", "cta_link", v)} />
          <Field label="Guide button label" value={form.hero?.guide_label} onChange={(v) => setObj("hero", "guide_label", v)} />
        </div>
        <Lines label="Trust bar items" value={form.trust_bar} onChange={(v) => set("trust_bar", v)} />
      </Card>

      <Card title="The Problem">
        <Field label="Heading" value={form.problem?.heading} onChange={(v) => setObj("problem", "heading", v)} />
        <Field label="Intro" value={form.problem?.intro} onChange={(v) => setObj("problem", "intro", v)} area />
        <Lines label="Points" value={form.problem?.points} onChange={(v) => setObj("problem", "points", v)} />
      </Card>

      <Card title="Services (spoke pages)">
        <Repeat items={form.services} onChange={(v) => set("services", v)} addLabel="Add service"
          template={{ slug: "", icon: "Wrench", title: "", intro: "", points: [], who: "", deliverables: [] }}
          render={(it, upd) => (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Title" value={it.title} onChange={(v) => upd({ title: v })} />
                <Field label="URL slug" value={it.slug} onChange={(v) => upd({ slug: v })} />
                <Field label="Icon (lucide name)" value={it.icon} onChange={(v) => upd({ icon: v })} />
              </div>
              <Field label="Intro" value={it.intro} onChange={(v) => upd({ intro: v })} area />
              <Lines label="What's included" value={it.points} onChange={(v) => upd({ points: v })} />
              <Field label="Who it's for" value={it.who} onChange={(v) => upd({ who: v })} area />
              <Lines label="What you get (deliverables)" value={it.deliverables} onChange={(v) => upd({ deliverables: v })} />
            </>
          )} />
      </Card>

      <Card title="How it works">
        <Field label="Heading" value={form.how_it_works?.heading} onChange={(v) => setObj("how_it_works", "heading", v)} />
        <Repeat items={form.how_it_works?.steps} onChange={(v) => setObj("how_it_works", "steps", v)} addLabel="Add step"
          template={{ title: "", text: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Step title" value={it.title} onChange={(v) => upd({ title: v })} />
              <Field label="Step text" value={it.text} onChange={(v) => upd({ text: v })} />
            </div>
          )} />
      </Card>

      <Card title="Why CASS + InTouch">
        <Field label="Why heading" value={form.why?.heading} onChange={(v) => setObj("why", "heading", v)} />
        <Repeat items={form.why?.points} onChange={(v) => setObj("why", "points", v)} addLabel="Add point"
          template={{ title: "", text: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Title" value={it.title} onChange={(v) => upd({ title: v })} />
              <Field label="Text" value={it.text} onChange={(v) => upd({ text: v })} />
            </div>
          )} />
        <div className="pt-2 border-t border-slate-100">
          <Field label="InTouch heading" value={form.intouch?.heading} onChange={(v) => setObj("intouch", "heading", v)} />
          <Field label="InTouch text" value={form.intouch?.text} onChange={(v) => setObj("intouch", "text", v)} area />
        </div>
      </Card>

      <Card title="InTouch + Xero Integration & Partnership">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.integration?.eyebrow} onChange={(v) => setObj("integration", "eyebrow", v)} />
          <Field label="Partner badge label" value={form.integration?.partner_badge} onChange={(v) => setObj("integration", "partner_badge", v)} />
        </div>
        <Field label="Heading" value={form.integration?.heading} onChange={(v) => setObj("integration", "heading", v)} />
        <Field label="Intro" value={form.integration?.intro} onChange={(v) => setObj("integration", "intro", v)} area />
        <Lines label="Points" value={form.integration?.points} onChange={(v) => setObj("integration", "points", v)} />
        <p className="text-xs text-slate-400">The InTouch and Xero logos are shown automatically.</p>
      </Card>

      <Card title="Case studies (Success Stories)">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Section heading" value={form.case_studies_heading} onChange={(v) => set("case_studies_heading", v)} />
          <Field label="Section intro" value={form.case_studies_intro} onChange={(v) => set("case_studies_intro", v)} />
        </div>
        <Repeat items={form.case_studies} onChange={(v) => set("case_studies", v)} addLabel="Add case study"
          template={{ slug: "", title: "", client: "", sector: "", published: true, logo: "", card_summary: "", software: [], services: [], challenge: [], solution_intro: "", solution_points: [], solution_outro: "", results_intro: "", results: [], stats: [], why_heading: "Why they chose CASS", why: [], benefits: [], closing_quote: "" }}
          render={(it, upd) => (
            <>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input type="checkbox" checked={it.published !== false} onChange={(e) => upd({ published: e.target.checked })} /> Published (shown on the site)
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Client / firm name" value={it.client} onChange={(v) => upd({ client: v })} />
                <Field label="URL slug (e.g. aconveyancing)" value={it.slug} onChange={(v) => upd({ slug: v })} />
              </div>
              <Field label="Title / headline" value={it.title} onChange={(v) => upd({ title: v })} />
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Sector" value={it.sector} onChange={(v) => upd({ sector: v })} />
                <Field label="Logo image URL" value={it.logo} onChange={(v) => upd({ logo: v })} />
              </div>
              <Field label="Card summary (short teaser on the hub card)" value={it.card_summary} onChange={(v) => upd({ card_summary: v })} area />
              <div className="grid md:grid-cols-2 gap-3">
                <Lines label="Software used" value={it.software} onChange={(v) => upd({ software: v })} />
                <Lines label="Services provided" value={it.services} onChange={(v) => upd({ services: v })} />
              </div>
              <Lines label="The Challenge (one paragraph per line)" value={it.challenge} onChange={(v) => upd({ challenge: v })} />
              <Field label="Solution — intro" value={it.solution_intro} onChange={(v) => upd({ solution_intro: v })} area />
              <Lines label="Solution — bullet points" value={it.solution_points} onChange={(v) => upd({ solution_points: v })} />
              <Field label="Solution — closing line" value={it.solution_outro} onChange={(v) => upd({ solution_outro: v })} area />
              <Field label="Results — intro" value={it.results_intro} onChange={(v) => upd({ results_intro: v })} area />
              <div>
                <span className="block text-xs font-semibold text-slate-500 mb-1">Results cards</span>
                <Repeat items={it.results} onChange={(v) => upd({ results: v })} addLabel="Add result"
                  template={{ icon: "Target", title: "", text: "" }}
                  render={(r, rup) => (
                    <>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Field label="Icon (Target, ShieldCheck, BarChart3, Zap, Handshake, TrendingUp…)" value={r.icon} onChange={(v) => rup({ icon: v })} />
                        <Field label="Title" value={r.title} onChange={(v) => rup({ title: v })} />
                      </div>
                      <Field label="Text" value={r.text} onChange={(v) => rup({ text: v })} area />
                    </>
                  )} />
              </div>
              <div>
                <span className="block text-xs font-semibold text-slate-500 mb-1">Key statistics (big number cards)</span>
                <Repeat items={it.stats} onChange={(v) => upd({ stats: v })} addLabel="Add statistic"
                  template={{ value: "", label: "" }}
                  render={(s, sup) => (
                    <div className="grid md:grid-cols-2 gap-3">
                      <Field label="Value (e.g. ~100)" value={s.value} onChange={(v) => sup({ value: v })} />
                      <Field label="Label" value={s.label} onChange={(v) => sup({ label: v })} />
                    </div>
                  )} />
              </div>
              <Field label="Why CASS — heading" value={it.why_heading} onChange={(v) => upd({ why_heading: v })} />
              <Lines label="Why CASS (one paragraph per line)" value={it.why} onChange={(v) => upd({ why: v })} />
              <Lines label="Key benefits (checklist)" value={it.benefits} onChange={(v) => upd({ benefits: v })} />
              <Field label="Closing quote" value={it.closing_quote} onChange={(v) => upd({ closing_quote: v })} area />
            </>
          )} />
        <p className="text-xs text-slate-400">Each case study shows as a card on the Legal Cashiering hub linking to its own full page at /legal-cashiering/case-study/&lt;slug&gt;. Give every case study a unique slug.</p>
      </Card>

      <Card title="FAQs">
        <Repeat items={form.faqs} onChange={(v) => set("faqs", v)} addLabel="Add FAQ"
          template={{ category: "General", question: "", answer: "", link_label: "", link_url: "" }}
          render={(it, upd) => (
            <>
              <Field label="Category" value={it.category} onChange={(v) => upd({ category: v })} />
              <Field label="Question" value={it.question} onChange={(v) => upd({ question: v })} />
              <Field label="Answer" value={it.answer} onChange={(v) => upd({ answer: v })} area />
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Optional button text" value={it.link_label} onChange={(v) => upd({ link_label: v })} placeholder="e.g. Learn more" />
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-500 mb-1">Optional link to page</span>
                  <PageLinkSelect value={it.link_url} onChange={(v) => upd({ link_url: v })} className={inputCls} />
                </label>
              </div>
            </>
          )} />
        <p className="text-xs text-slate-400">FAQs are grouped on the page by Category. Use the same wording (e.g. "General", "Compliance", "Day-to-Day Service", "Security", "Pricing", "About CASS", "Getting Started") to keep them together.</p>
      </Card>

      <Card title="Enquiry CTA">
        <Field label="Heading" value={form.cta?.heading} onChange={(v) => setObj("cta", "heading", v)} />
        <Field label="Text" value={form.cta?.text} onChange={(v) => setObj("cta", "text", v)} area />
      </Card>

      <Card title="Lead magnet">
        <label className="flex items-center gap-2 text-sm font-semibold text-navy">
          <input type="checkbox" checked={!!form.lead_magnet?.enabled} onChange={(e) => setObj("lead_magnet", "enabled", e.target.checked)} /> Show lead-magnet block
        </label>
        {lmStats && (
          <div className="flex items-center gap-3 rounded-xl bg-teal/5 border border-teal/20 px-4 py-3" data-testid="lca-lead-magnet-stats">
            <span className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center shrink-0"><Inbox size={18} /></span>
            <div className="flex-1">
              <div className="text-2xl font-display font-extrabold text-navy leading-none" data-testid="lca-lead-magnet-count">{lmStats.count}</div>
              <div className="text-xs text-slate-500 mt-0.5">checklist download{lmStats.count === 1 ? "" : "s"} captured{lmStats.recent?.[0] ? ` · latest: ${lmStats.recent[0].email}` : ""}</div>
            </div>
            {lmStats.count > 0 && (
              <button type="button" onClick={exportLeads} data-testid="lca-lead-magnet-export" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal hover:text-teal-dark shrink-0"><Download size={15} /> Export CSV</button>
            )}
          </div>
        )}
        <Field label="Heading" value={form.lead_magnet?.heading} onChange={(v) => setObj("lead_magnet", "heading", v)} />
        <Field label="Text" value={form.lead_magnet?.text} onChange={(v) => setObj("lead_magnet", "text", v)} area />
        <PdfUpload
          value={form.lead_magnet?.file_url}
          filename={form.lead_magnet?.filename}
          onChange={(url, name) => setForm((f) => ({ ...f, lead_magnet: { ...(f.lead_magnet || {}), file_url: url, filename: name } }))}
        />
        <Field label="Linked guide slug (from Guides — optional fallback)" value={form.lead_magnet?.guide_slug} onChange={(v) => setObj("lead_magnet", "guide_slug", v)} />
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-navy">
            <input type="checkbox" data-testid="lca-followup-enabled" checked={!!form.lead_magnet?.followup_enabled} onChange={(e) => setObj("lead_magnet", "followup_enabled", e.target.checked)} /> Send a follow-up email 3 days after download
          </label>
          <p className="text-xs text-slate-400 -mt-1">A friendly "did the checklist help?" email, sent automatically 3 days later. Only new downloads (going forward) receive it.</p>
          <Field label="Follow-up subject" value={form.lead_magnet?.followup_subject} onChange={(v) => setObj("lead_magnet", "followup_subject", v)} />
          <Field label="Follow-up message" value={form.lead_magnet?.followup_body} onChange={(v) => setObj("lead_magnet", "followup_body", v)} area />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Button label" value={form.lead_magnet?.followup_cta_label} onChange={(v) => setObj("lead_magnet", "followup_cta_label", v)} placeholder="Book a discovery call" />
            <Field label="Button link" value={form.lead_magnet?.followup_cta_link} onChange={(v) => setObj("lead_magnet", "followup_cta_link", v)} placeholder="/legal-cashiering#enquire" />
          </div>
        </div>
      </Card>

      <Card title="SEO">
        <Field label="Meta title" value={form.seo?.title} onChange={(v) => setObj("seo", "title", v)} />
        <Field label="Meta description" value={form.seo?.description} onChange={(v) => setObj("seo", "description", v)} area />
      </Card>

      <div className="flex justify-end mt-2">
        <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save changes
        </Button>
      </div>
    </div>
  );
}

function Enquiries() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    adminApi.enquiries.list().then((all) => setItems(all.filter((e) => e.division === "Legal Cashiering"))).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!items) return <div className="text-slate-400 py-10 text-center">Loading…</div>;
  if (!items.length) return <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-400" data-testid="lca-enquiries-empty">No Legal Cashiering enquiries yet.</div>;

  return (
    <div className="space-y-3" data-testid="lca-enquiries">
      {items.map((e) => (
        <div key={e.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-display font-bold text-navy">{e.name} {e.business_name ? <span className="text-slate-400 font-normal">· {e.business_name}</span> : null}</p>
            <span className="text-xs text-slate-400">{(e.created_at || "").slice(0, 10)}</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">{e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
          <p className="text-sm text-slate-600 mt-3 whitespace-pre-line">{e.message}</p>
        </div>
      ))}
    </div>
  );
}

export default function LegalCashieringAdmin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("content");

  const tabs = [
    { key: "content", label: "Content", icon: FileText },
    { key: "enquiries", label: "Enquiries", icon: Inbox },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-navy">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <button data-testid="lca-back-admin" onClick={() => navigate("/admin")} className="flex items-center gap-2.5 text-slate-200 hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <img src={logoLight} alt="CASS" className="h-8 w-auto" />
            <span className="text-xs text-slate-400 hidden sm:inline flex items-center gap-1"><Scale size={12} /> Legal Cashiering Admin</span>
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <a href="/legal-cashiering" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/10"><ExternalLink size={16} /><span className="hidden sm:inline">View page</span></a>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/10"><LogOut size={16} /><span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-7 w-1.5 rounded-full bg-orange" />
          <h1 className="font-display font-extrabold text-2xl md:text-3xl text-navy">CASS Legal Cashiering</h1>
        </div>

        <div className="flex gap-2 mb-6">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`lca-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy hover:border-orange/40"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "content" ? <ContentEditor /> : <Enquiries />}
      </main>
    </div>
  );
}
